import React from 'react';
import {
	getTemplateLanguageCode,
	isTemplateApproved,
	sortTemplateComponents,
} from '../helpers/TemplateMessageHelper';
import { Button, Tooltip } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { SendButton } from '@src/components/SavedResponseList/SavedResponseList.styles';
import { useTranslation } from 'react-i18next';
import useTemplates from '@src/hooks/useTemplates';
import { useAppSelector } from '@src/store/hooks';
import { Template } from '@src/types/templates';
import * as StyledChatMessage from '@src/components/Main/Chat/ChatMessage/ChatMessage.styles';
import { MessageType } from '@src/types/messages';

interface Props {
	onClick: (template: Template) => void;
	customSelectButtonTitle?: string;
	displayRegisterTemplate?: boolean;
}

const TemplatesList: React.FC<Props> = ({
	onClick,
	customSelectButtonTitle,
	displayRegisterTemplate,
}) => {
	const { t } = useTranslation();

	const { isTemplatesFailed } = useAppSelector((state) => state.UI);
	const templates = useAppSelector((state) => state.templates.value);
	const selectableTemplates = Object.entries(templates)
		.filter(([, template]) => template.staff_visible !== false && isTemplateApproved(template))
		.sort((a, b) => (a[1].staff_order ?? 100) - (b[1].staff_order ?? 100));

	const { issueTemplateRefreshRequest } = useTemplates();

	const isRefreshingTemplates = useAppSelector(
		(state) => state.isRefreshingTemplates.value
	);

	const doRefreshTemplates = async () => {
		await issueTemplateRefreshRequest();
	};

	return (
		<div className="templateMessagesWrapper">
			<div className="templateMessages" data-testid="template-messages">
				{window.config?.APP_ENV_NAME !== 'cvparc-shadow' && <div className="templateMessages__actions">
					<div className="templateMessages__create">
						{displayRegisterTemplate && (
							<Button
								// @ts-ignore
								color="black"
								href="https://hub.360dialog.com/dashboard/home"
								target="_blank"
								size="small"
							>
								{t('Register new template')}
							</Button>
						)}
					</div>

					{window.config?.APP_ENV_NAME !== 'cvparc-shadow' && <div className="templateMessages__refresh">
						{isRefreshingTemplates ? (
							<span>Refreshing templates...</span>
						) : (
							<>
								<span>{window.config?.APP_ENV_NAME === 'cvparc-shadow' ? 'Catálogo del equipo · Sincronización automática' : t('Not seeing your new templates?')}</span>
								<a onClick={(event) => { event.preventDefault(); void doRefreshTemplates(); }} href="#">
									{window.config?.APP_ENV_NAME === 'cvparc-shadow' ? 'Actualizar lista' : t('Click here to refresh')}
								</a>
							</>
						)}
					</div>}
				</div>}

				{selectableTemplates.length === 0 && (
					<div className="templateMessages__emptyInfo mt-3">
						{isTemplatesFailed ? (
							<span>{t("Template messages couldn't be loaded.")}</span>
						) : (
							<span>{t('No templates have been registered yet.')}</span>
						)}
					</div>
				)}

				{selectableTemplates.map((template) => (
					window.config?.APP_ENV_NAME === 'cvparc-shadow' ?
					<button key={template[0]} type="button" className="clinic-template-choice" onClick={() => onClick(template[1])}>
						<span>{template[1].display_name ?? template[1].name}</span><span aria-hidden="true">›</span>
					</button> :
					<div key={template[0]} className="templateMessageWrapper">
						<StyledChatMessage.ChatMessage
							$type={MessageType.template}
							$isOutgoing={true}
						>
							{/*<span className={"templateMessage__status " + template[1].status}>{template[1].status}</span>*/}
							<div className="templateMessage__message">
								<h4>
									{template[1].display_name ?? template[1].name}
									{getTemplateLanguageCode(template[1].language) && (
										<span className="templateMessage__language">
											{' '}
											({getTemplateLanguageCode(template[1].language)})
										</span>
									)}
								</h4>
								{window.config?.APP_ENV_NAME !== 'cvparc-shadow' && sortTemplateComponents(template[1].components ?? []).map(
									(comp: any, index: number) => (
										<div key={index}>
											<span className="templateType bold lowercase">
												{comp.type}:
											</span>{' '}
											{comp.text ?? comp.format ?? JSON.stringify(comp.buttons)}
										</div>
									)
								)}
							</div>
						</StyledChatMessage.ChatMessage>
						{isTemplateApproved(template[1]) && (
							<Tooltip title={customSelectButtonTitle ?? t('Send')}>
								<SendButton
									aria-label={customSelectButtonTitle ?? t('Send')}
									onClick={() => {
										console.log('Selected template', template[1]);
										onClick?.(template[1]);
									}}
									size="small"
								>
									<SendIcon />
								</SendButton>
							</Tooltip>
						)}
					</div>
				))}
			</div>
		</div>
	);
};

export default TemplatesList;
